import { provinceOrStateCodeFromSlug, countryCodeFromProvinceOrState, provinceOrStateNameFromCode } from 'utils/province_or_state';
import type { ProvinceOrStateCode } from 'utils/province_or_state';

/**
 * Interface defining the structure for polygon slugs
 */
export interface PolygonSlugs {
  province: string | null;
  subDivision: string | null;
  neighbourhood: string | null;
}

/**
 * @typedef {object} PolygonSlugs
 * @property {string | null} province - Province/State slug (e.g., "ns-ca", "ny-us")
 * @property {string | null} subDivision - Sub-division/City slug (e.g., "halifax-ns-ca", "toronto-on-ca")
 * @property {string | null} neighbourhood - Neighbourhood slug (e.g., "downtown-halifax-ns-ca")
 *
 * Generates polygon slugs from a given pathname.
 *
 * The function parses the pathname and constructs slugs for different
 * geographical levels: province/state, subDivision, and neighbourhood.
 *
 * Since this function cannot determine if the second part is a neighbourhood or the address
 * it will return neighbourhood slug as is, so it's not guaranteed to be a correct neighbourhood slug
 *
 * Slug Formats:
 * - Province/State: `province_or_state_slug-country_code` (e.g., "ns-ca", "ny-us")
 * - SubDivision: `subdivision_slug-province_or_state_slug-country_code` (e.g., "halifax-ns-ca")
 * - Neighbourhood: `neighbourhood_slug-subdivision_slug-province_or_state_slug-country_code` (e.g., "downtown-halifax-ns-ca")
 *
 * @param {string} pathname - The URL pathname (e.g., "/toronto-on-real-estate/downtown")
 * @returns {PolygonSlugs} An object containing slugs for different geographical levels.
 *                         Returns null for levels not present in the pathname.
 */
export function generatePolygonSlugsFromPathname(pathname: string): PolygonSlugs {
  const slugs: PolygonSlugs = {
    province: null,
    subDivision: null,
    neighbourhood: null,
  };

  const pathParts = pathname.split('/').filter(Boolean);
  if (pathParts.length === 0) {
    return slugs;
  }

  let baseSlugWithRealEstate = '';
  let neighbourhoodPart = null;

  if (pathParts[0] && pathParts[0].includes('-real-estate')) {
    baseSlugWithRealEstate = pathParts[0];
    if (pathParts.length > 1) {
      neighbourhoodPart = pathParts[1];
    }
  }

  if (!baseSlugWithRealEstate) {
    return slugs;
  }

  const baseSlug = baseSlugWithRealEstate.replace('-real-estate', '');

  // Code below can be moved to standalone function, generatePolygonSlugsFromPathSlug

  // 1. Check if the baseSlug directly corresponds to a known province/state slug
  const provinceCodeFromBaseSlug = provinceOrStateCodeFromSlug(baseSlug);

  if (provinceCodeFromBaseSlug) {
    const country = countryCodeFromProvinceOrState(provinceCodeFromBaseSlug);
    slugs.province = `${provinceCodeFromBaseSlug}-${country.toLowerCase()}`;
    return slugs;
  }

  // 2. If that slug wasn't a province/state slug, means it should be a city-province/state slug
  const partsOfBaseSlug = baseSlug.split('-');
  if (partsOfBaseSlug.length > 1) {
    const potentialProvinceAbbrev = partsOfBaseSlug[partsOfBaseSlug.length - 1];
    const fullProvinceName = provinceOrStateNameFromCode(potentialProvinceAbbrev as ProvinceOrStateCode);

    // That code is valid province code, so we can use it to generate the slug
    if (fullProvinceName) {
      const country = countryCodeFromProvinceOrState(potentialProvinceAbbrev);
      slugs.province = `${potentialProvinceAbbrev}-${country}`.toLowerCase();
      slugs.subDivision = `${baseSlug}-${country.toLowerCase()}`;

      if (neighbourhoodPart) {
        slugs.neighbourhood = `${neighbourhoodPart}-${slugs.subDivision}`;
      }
      return slugs;
    }
  }
  return slugs;
}